import { supabase } from '../lib/supabase';
import { updateProductsQuantityFromLocations } from './locationInventory';

interface TransferItemInput {
  productId: string;
  quantity: number;
  sourceLocationId: string;
  destinationLocationId: string;
}

interface TransferResult {
  success: boolean;
  error?: string;
  sourceNewQuantity?: number;
  destinationNewQuantity?: number;
}

interface ValidationResult {
  valid: boolean;
  errors: Array<{
    productId: string;
    productName?: string;
    error: string;
    availableQuantity?: number;
    requestedQuantity?: number;
  }>;
}

export const validateTransferAvailability = async (
  items: TransferItemInput[],
  adminId: string
): Promise<ValidationResult> => {
  const errors: ValidationResult['errors'] = [];

  for (const item of items) {
    const { data: locationQty, error } = await supabase
      .from('product_location_inventory')
      .select('quantity')
      .eq('product_id', item.productId)
      .eq('location_id', item.sourceLocationId)
      .maybeSingle();

    if (error) {
      errors.push({
        productId: item.productId,
        error: 'Failed to check inventory'
      });
      continue;
    }

    const availableQty = locationQty?.quantity || 0;
    if (availableQty < item.quantity) {
      const { data: product } = await supabase
        .from('products')
        .select('name')
        .eq('id', item.productId)
        .maybeSingle();

      errors.push({
        productId: item.productId,
        productName: product?.name,
        error: 'Insufficient stock at source location',
        availableQuantity: availableQty,
        requestedQuantity: item.quantity
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

export const transferBetweenLocations = async (
  productId: string,
  quantity: number,
  sourceLocationId: string,
  destinationLocationId: string,
  adminId: string
): Promise<TransferResult> => {
  try {
    const { data: sourceQty, error: sourceError } = await supabase
      .from('product_location_inventory')
      .select('quantity')
      .eq('product_id', productId)
      .eq('location_id', sourceLocationId)
      .maybeSingle();

    if (sourceError) throw sourceError;

    const currentSourceQty = sourceQty?.quantity || 0;
    if (currentSourceQty < quantity) {
      return {
        success: false,
        error: `Insufficient quantity at source location (available: ${currentSourceQty})`
      };
    }

    const newSourceQty = currentSourceQty - quantity;

    if (sourceQty) {
      const { error: updateSourceError } = await supabase
        .from('product_location_inventory')
        .update({ quantity: newSourceQty, updated_at: new Date().toISOString() })
        .eq('product_id', productId)
        .eq('location_id', sourceLocationId);

      if (updateSourceError) throw updateSourceError;
    }

    const { data: destQty, error: destError } = await supabase
      .from('product_location_inventory')
      .select('quantity')
      .eq('product_id', productId)
      .eq('location_id', destinationLocationId)
      .maybeSingle();

    if (destError) throw destError;

    const currentDestQty = destQty?.quantity || 0;
    const newDestQty = currentDestQty + quantity;

    if (destQty) {
      const { error: updateDestError } = await supabase
        .from('product_location_inventory')
        .update({ quantity: newDestQty, updated_at: new Date().toISOString() })
        .eq('product_id', productId)
        .eq('location_id', destinationLocationId);

      if (updateDestError) throw updateDestError;
    } else {
      const { error: insertDestError } = await supabase
        .from('product_location_inventory')
        .insert({
          product_id: productId,
          location_id: destinationLocationId,
          quantity: newDestQty,
          admin_id: adminId
        });

      if (insertDestError) throw insertDestError;
    }

    await updateProductsQuantityFromLocations(productId, adminId);

    return {
      success: true,
      sourceNewQuantity: newSourceQty,
      destinationNewQuantity: newDestQty
    };
  } catch (error) {
    console.error('Transfer error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Transfer failed'
    };
  }
};

interface TransferUpToAvailableResult {
  requested: number;
  available: number;
  transferred: number;
  error?: string;
}

export const transferUpToAvailable = async (
  productId: string,
  requestedQuantity: number,
  sourceLocationId: string,
  destinationLocationId: string,
  adminId: string
): Promise<TransferUpToAvailableResult> => {
  if (requestedQuantity <= 0) {
    return { requested: requestedQuantity, available: 0, transferred: 0 };
  }

  const { data: sourceQty, error: sourceError } = await supabase
    .from('product_location_inventory')
    .select('quantity')
    .eq('product_id', productId)
    .eq('location_id', sourceLocationId)
    .maybeSingle();

  if (sourceError) {
    return {
      requested: requestedQuantity,
      available: 0,
      transferred: 0,
      error: sourceError.message,
    };
  }

  const available = Math.max(0, sourceQty?.quantity || 0);
  const transferred = Math.min(requestedQuantity, available);

  if (transferred <= 0) {
    return { requested: requestedQuantity, available, transferred: 0 };
  }

  const result = await transferBetweenLocations(
    productId,
    transferred,
    sourceLocationId,
    destinationLocationId,
    adminId
  );

  if (!result.success) {
    return {
      requested: requestedQuantity,
      available,
      transferred: 0,
      error: result.error,
    };
  }

  return { requested: requestedQuantity, available, transferred };
};

export const batchTransferItems = async (
  items: TransferItemInput[],
  adminId: string
): Promise<{ success: boolean; error?: string; results?: TransferResult[] }> => {
  const validation = await validateTransferAvailability(items, adminId);
  if (!validation.valid) {
    const errorMessages = validation.errors
      .map(e => `${e.productName || e.productId}: ${e.error}`)
      .join('; ');
    return { success: false, error: errorMessages };
  }

  const results: TransferResult[] = [];

  for (const item of items) {
    const result = await transferBetweenLocations(
      item.productId,
      item.quantity,
      item.sourceLocationId,
      item.destinationLocationId,
      adminId
    );
    results.push(result);

    if (!result.success) {
      return { success: false, error: result.error, results };
    }
  }

  return { success: true, results };
};

export const reverseTransfer = async (
  items: TransferItemInput[],
  adminId: string
): Promise<{ success: boolean; error?: string }> => {
  const reversedItems = items.map(item => ({
    ...item,
    sourceLocationId: item.destinationLocationId,
    destinationLocationId: item.sourceLocationId
  }));

  return batchTransferItems(reversedItems, adminId);
};

export const generateTransferNumber = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 9999).toString().padStart(4, '0');
  return `TRF-${year}${month}${day}-${random}`;
};
